import type { OrigenDelVinculo } from '../services/link-review-service';

export type DynamicPricingTab =
  | 'overview'
  | 'products'
  | 'competitors'
  | 'charts'
  | 'catalog'
  | 'inference'
  | 'monitoring'
  | 'api';

export type PriceStrategy =
  | 'MATCH_LOWEST'
  | 'UNDERCUT_PERCENT'
  | 'MARGIN_TARGET'
  | 'CUSTOM_FORMULA'
  | 'SMART_PRICING';

export type AlertSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export type DataSourceType = 'SCRAPING' | 'API' | 'MANUAL';

export interface DynamicProduct {
  id: string;
  name: string;
  sku: string;
  category: string | null;
  /** La marca (4B-datos T12b). Opcional: un backend viejo no la manda. */
  brand?: string | null;
  /** La cargó una persona: el relleno desde el catálogo no la pisa. */
  brandManual?: boolean;
  currentPrice: number;
  basePrice: number | null;
  costPrice: number | null;
  stock: number | null;
  minStock: number | null;
  leadTimeDays: number | null;
  monthlySalesVelocity: number | null;
  targetMargin: number | null;
  imageUrl: string | null;
  /** La página del producto en la tienda online de la empresa (Monitoreo, 25-oct). */
  productUrl?: string | null;
  /** Plan 268: la URL que mandó el cliente; `imageUrl` es la copia propia. */
  imageSourceUrl?: string | null;
  imageStatus?: 'PENDING' | 'READY' | 'FAILED' | null;
  imageError?: string | null;
  description: string | null;
  isActive: boolean;
  /** Producto de ejemplo de la demo (códigos DEMO-xxx). Al pasar al plan completo queda: se archiva a mano. */
  esDeEjemplo?: boolean;
  strategy: PriceStrategy;
  strategyConfig: Record<string, any> | null;
  pricingModelId: string | null;
  pricingModel: { id: string; name: string } | null;
  lastCalculatedPrice: number | null;
  lastCalculatedAt: string | null;
  competitorProducts?: DynamicCompetitorProduct[];
  /**
   * Tiendas que dejaron de vender el producto. Va aparte de `competitorProducts` a
   * propósito: son precios que ya no existen, así que no cuentan para los KPIs ni para
   * el orden por diferencia — solo para que la celda diga "retirado" en vez de vacío.
   */
  retiredCompetitors?: RetiredCompetitorProduct[];
  _count: { competitorProducts: number; calculatedPrices?: number };
  createdAt: string;
  updatedAt: string;
}

/**
 * Lo que el backend revisó al guardar (4B-datos T11): las reglas que rompe el
 * precio guardado quedan marcadas en la ficha. `revisadas: false` = no cambió el
 * precio o no se pudo revisar (no se sabe; nunca «no rompe nada»).
 */
export type ReglasDelGuardado =
  | {
      revisadas: true;
      rompe: Array<{
        ruleId: string;
        ruleName: string;
        tipo: string;
        min: number | null;
        max: number | null;
      }>;
    }
  | { revisadas: false; motivo: 'SIN_CAMBIO_DE_PRECIO' | 'NO_SE_PUDO' };

/** Lo que devuelve PUT /products/:id (4B-datos T11): con `sacarDeEstrategias`, de cuáles lo sacó. */
export type ProductoGuardado = DynamicProduct & {
  sacadoDe?: Array<{ id: string; name: string }>;
  /** Estrategias manuales que quedaron sin productos: avisarlo. */
  quedanVacias?: Array<{ id: string; name: string }>;
  /** Ruling R7: el formulario avisa con esto «quedó marcado en su ficha». */
  reglas?: ReglasDelGuardado;
  /**
   * Sólo del frontend (lo pone el store, no el backend): se guardó, pero la lista
   * de productos no se pudo volver a leer. El formulario lo dice sin «No se guardó».
   */
  listaSinActualizar?: boolean;
};

/**
 * Datos del artículo de un competidor vinculado a un producto propio, tal como
 * llegan enriquecidos en GET /api/dynamic-pricing/products.
 * Los campos nuevos son opcionales para tolerar respuestas anteriores del backend.
 */
export interface DynamicCompetitorProduct {
  productId?: string;
  storeId: string;
  lastScrapedPrice: number | null;
  lastListPrice?: number | null;
  externalUrl?: string | null;
  /** La última vez que el precio CAMBIÓ (o lo reescribió un barrido). */
  lastScrapedAt?: string | null;
  /** L3 (vault 276): la última vez que el chequeo lo CONFIRMÓ, aunque no haya cambiado. */
  lastConfirmedAt?: string | null;
  /** Cantidad de chequeos consecutivos en que no se encontró el artículo. */
  consecutiveNotFound?: number;
  /** Motivo por el que el artículo no está disponible (deslistado, 404, etc.). */
  unavailableReason?: string | null;
  /** Nombre del artículo tal como aparece en la tienda del competidor. */
  matchedName?: string | null;
  /**
   * Confianza con la que se creó ESTE vínculo (0-100, L1 `matchScoreAtLink`). null si
   * no hay dato (vinculado a mano o vínculo viejo sin rastro): nunca la de otro artículo.
   */
  matchConfidence?: number | null;
  /** Motivos de la propuesta que creó este vínculo. */
  matchReasons?: string[];
  /** Cómo nació el vínculo (L1). */
  linkOrigin?: OrigenDelVinculo | null;
  /** Cuándo lo revisó una persona (L1). */
  reviewedAt?: string | null;
  store?: { id: string; name: string };
}

/**
 * Vínculo con una tienda que se dio de baja: la tienda dejó de vender el producto y
 * ningún vínculo vigente la reemplazó. Se conserva el último precio visto como
 * referencia histórica.
 */
export interface RetiredCompetitorProduct {
  productId?: string;
  storeId: string;
  /** 26-oct: la página del producto en esa tienda (puede ya no estar publicada). */
  externalUrl?: string | null;
  lastScrapedPrice: number | null;
  lastScrapedAt?: string | null;
  /** Cuándo se dio de baja el vínculo. */
  archivedAt?: string | null;
  /** Por qué se dio de baja. Los vínculos archivados antes de julio 2026 no lo tienen. */
  unavailableReason?: string | null;
}

/** Grupo de filtro "Vínculo": si el producto está asociado a alguna tienda. */
export type LinkState = 'all' | 'linked' | 'unlinked';

/**
 * Grupo de filtro "Confianza del dato".
 * - fresh: tiene al menos un precio confirmado en los últimos 30 días.
 * - stale: tiene precios, pero todos con más de 30 días.
 * - retired: alguna tienda dejó de venderlo.
 */
export type DataState = 'all' | 'fresh' | 'stale' | 'retired';

/** Los números que van dentro de los chips de la barra de filtros. */
export interface ProductFilterCounts {
  link: { all: number; linked: number; unlinked: number };
  data: { fresh: number; stale: number; retired: number };
  /** Cuántos quedarían prendiendo cada chip de «Otros» (L4). */
  otros?: {
    calculated: number;
    onOffer: number;
    lowConfidence: number;
    withError: number;
    withoutCompetitor: number;
  };
}

/** Estado de salud de una tienda competidora. */
export type CompetitorStoreStatus = 'ok' | 'stale' | 'never' | 'error';

export interface CompetitorStoreRef {
  id: string;
  name: string;
  /** Tipo de scraper (Magento, VTEX, Shopify…). */
  scraperType?: string | null;
  /** Productos propios vinculados con esta tienda (la definición única, L4). */
  linkedCount?: number;
  /** De esos, con un precio leído. */
  withPriceCount?: number;
  /** Última vez que se scrapeó esta tienda. */
  lastScrapedAt?: string | null;
  /** Estado de salud derivado por el backend. */
  status?: CompetitorStoreStatus;
}

export interface ProductsResponse {
  products: DynamicProduct[];
  total: number;
  page: number;
  totalPages: number;
  competitorStores: CompetitorStoreRef[];
}

export interface CompetitorStore {
  id: string;
  name: string;
  url: string;
  logoUrl: string | null;
  isActive: boolean;
  isOwnCatalog: boolean;
  includeInAnalysis: boolean;
  scraperType: string;
  /** La tienda del catálogo de Prixia de la que esta es la copia de la empresa (`null`: propia de la empresa). */
  prixiaStoreId?: string | null;
  /** Cuándo la plataforma fusionó esta copia en otra de la misma tienda (`null`: no está fusionada). */
  mergedAt?: string | null;
  /**
   * Con qué categoría lista la plataforma a este local: "Hamburguesas", "Pollos".
   * La manda el descubrimiento de zona; es lo que permite podar la lista sin abrir
   * local por local. `null` en los que se cargaron a mano o antes de que existiera.
   */
  categoria?: string | null;
  /** Ciudad del local, derivada de su dirección. Todavía nada filtra por acá. */
  ciudad?: string | null;
  priceMonitorEnabled: boolean;
  priceCheckIntervalHours: number;
  rescrapeIntervalDays: number;
  _count: { products: number };
  /** Sólo los vínculos vigentes (la regla única, L4): `_count.products` ya no cuenta los archivados. */
  vinculados?: number;
  /** De esos, con un precio leído. */
  vinculadosConPrecio?: number;
  createdAt: string;
  /** Último SCRAPING, no cualquier job: spec 199 §5 separa las procedencias */
  lastJobAt: string | null;
  lastJobCount: number | null;
  lastInferenceAt: string | null;
  /** Última carga manual (spec 199 §5). `null` si la tienda nunca tuvo una. */
  lastManualLoadAt: string | null;
  lastManualLoadCount: number | null;
  /** Para abrir el panel de identificación desde la tarjeta */
  ultimaCargaManualJobId: string | null;
  /** Cuántos renglones de esa carga esperan una decisión (§4.2) */
  renglonesSinIdentificar: number;
  /** Plan 298 (C8): cómo y cuándo la lee Prixia. Sólo en las de Prixia (`prixiaStoreId`, S2). */
  prixia?: EstadoDeLecturaPrixia | null;
}

export type TipoDeLecturaPrixia = 'PRICE_CHECK' | 'FULL_RESCRAPE';

/** Cada cuánto se lee una tienda de Prixia (plan 298). */
export interface ProgramaDeLecturaPrixia {
  intervalValue: number;
  intervalUnit: 'HOURS' | 'DAYS' | 'WEEKS';
  preferredHour: number | null;
  preferredMin: number;
  isActive: boolean;
  /** Vacía si la lectura o la tienda están apagadas. */
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
}

/** Lo que la empresa ve de la lectura de una tienda de Prixia (C8). Nunca quién la pidió (C10). */
export interface EstadoDeLecturaPrixia {
  storeId: string;
  /** false = la plataforma la pausó: Prixia no la lee para nadie. */
  active: boolean;
  lastCheckAt: string | null;
  lastSweepAt: string | null;
  check: ProgramaDeLecturaPrixia | null;
  sweep: ProgramaDeLecturaPrixia | null;
  inProgress: { type: TipoDeLecturaPrixia; state: 'QUEUED' | 'RUNNING'; since: string } | null;
}

/** Cupo de «leer ahora», permiso «Monitoreo de lecturas», mínimos y topes (C8, C9). */
export interface AccesoPrixia {
  readingMonitor: boolean;
  canChangeFrequency: boolean;
  /** D-291-11: el barrido completo lo piden sólo los administradores. */
  isAdmin: boolean;
  readNow: { dailyQuota: number; usedToday: number; remainingToday: number };
  minimums: { checkHours: number; sweepHours: number };
  /** Tope de la empresa (8-oct): lo da el servidor, la pantalla no guarda una copia. */
  maximums: { checkHours: number; sweepHours: number };
}

/** Una lectura de una tienda de Prixia vista por la empresa (con el permiso). */
export interface LecturaPrixiaDeLaEmpresa {
  id: string;
  /** La copia de la empresa, no la tienda de Prixia. */
  storeId: string;
  storeName: string;
  type: TipoDeLecturaPrixia;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  source: 'SCHEDULE' | 'YOUR_COMPANY' | 'PRIXIA';
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  itemsRead: number;
  pricePointsWritten: number;
  notFound: number;
  /** L3 (vault 276): lo que la tienda no respondió en un chequeo. */
  noResponse?: number;
  errorMessage: string | null;
  note: string | null;
}

export interface ArticuloDeNovedad {
  id: string;
  name: string;
  price: number | null;
  productUrl: string | null;
}

/** C14: qué trajo de nuevo el último barrido y qué ya no se publica. */
export interface NovedadesDeLaTienda {
  readingId: string;
  finishedAt: string | null;
  comparable: boolean;
  newCount: number;
  removedCount: number;
  newItems: ArticuloDeNovedad[];
  removedItems: ArticuloDeNovedad[];
}

export interface MissingCompetitorItem {
  id: string;
  /** La página del producto en la tienda (la manda GET /competitors/:id/missing-items). */
  externalUrl?: string | null;
  lastScrapedAt: string | null;
  product: { id: string; name: string; sku: string; currentPrice: number };
  scrapedPrices: { price: number; scrapedAt: string }[];
}

export interface CompetitorProduct {
  id: string;
  externalUrl: string;
  cssSelector: string | null;
  lastScrapedPrice: number | null;
  lastListPrice?: number | null;
  lastScrapedAt: string | null;
  isActive: boolean;
  productId: string;
  storeId: string;
  store: {
    id: string;
    name: string;
    url: string;
    logoUrl?: string | null;
    isOwnCatalog?: boolean;
    includeInAnalysis?: boolean;
  };
  createdAt: string;
}

export interface ScrapedPrice {
  id: string;
  price: number;
  previousPrice: number | null;
  changePercent: number | null;
  scrapedAt: string;
}

export interface ExternalDataSource {
  id: string;
  name: string;
  type: DataSourceType;
  apiUrl: string | null;
  apiHeaders: Record<string, string> | null;
  apiResponsePath: string | null;
  refreshIntervalMinutes: number;
  isActive: boolean;
  lastSyncAt: string | null;
  lastValue: number | null;
  variableCode: string;
  createdAt: string;
}

export interface CalculatedPrice {
  id: string;
  recommendedPrice: number;
  currentPrice: number;
  competitorMinPrice: number | null;
  competitorAvgPrice: number | null;
  variablesSnapshot: Record<string, any> | null;
  formulaUsed: string | null;
  calculatedAt: string;
}

export interface PriceAlert {
  id: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  productId: string | null;
  product: { name: string; sku: string } | null;
  createdAt: string;
}

export interface DynamicPriceLog {
  id: string;
  action: string;
  previousValue: string | null;
  newValue: string | null;
  triggerPrice: number;
  details: Record<string, unknown> | null;
  productId: string | null;
  createdAt: string;
}

export interface ScrapeResult {
  /** Vínculos que se abrieron (sin los de tiendas con chequeo diario). */
  total: number;
  success: number;
  failed: number;
  /** La página dice agotado: el vínculo queda sin precio de mercado. */
  outOfStock: number;
  /** Tiendas que no se abren acá: su lector las chequea cada día, con disponibilidad. */
  skippedStores: { name: string; count: number }[];
  errors: ScrapeError[];
}

export interface ScrapeError {
  competitorProductId: string;
  storeName: string;
  url: string;
  reason: string;
}

export interface DynamicPricingStatus {
  activeProducts: number;
  competitors: number;
  lastActivity: string | null;
  lastAction: string | null;
}

export interface CreateProductInput {
  name: string;
  sku: string;
  category?: string;
  /** 4B-datos T12b: con trim en el backend; null la borra. */
  brand?: string | null;
  currentPrice: number;
  /** null = sin costo (el backend no acepta 0: un costo 0 del formulario viaja como null). */
  costPrice?: number | null;
  stock?: number;
  minStock?: number;
  leadTimeDays?: number;
  monthlySalesVelocity?: number;
  targetMargin?: number;
  imageUrl?: string;
  description?: string | null;
  strategy?: PriceStrategy;
  strategyConfig?: Record<string, any>;
  pricingModelId?: string;
}

export interface CreateCompetitorInput {
  name: string;
  url: string;
  logoUrl?: string;
  includeInAnalysis?: boolean;
  /**
   * Marca este local como el PROPIO del cliente — su página en la plataforma.
   * El recolector lo lee como catálogo propio y su menú se vuelve su carta.
   */
  isOwnCatalog?: boolean;
  /**
   * Partner id de la plataforma. Cuando el local se elige del buscador ya se
   * conoce, y pasarlo hace que la primera lectura sea por API (3 KB) en vez de
   * abrir la página (10 MB).
   */
  idPartner?: string | null;
  /** Categoría con la que la plataforma lista el local ("Hamburguesas"). */
  categoria?: string | null;
  /** Ciudad del local. */
  ciudad?: string | null;
}

/**
 * Un local del catálogo global, tal como lo devuelve el buscador
 * (`GET /api/locales/buscar`). Es el universo de locales conocidos de la ciudad;
 * elegir uno crea el CompetitorStore del cliente.
 */
export interface LocalDelCatalogo {
  url: string;
  nombre: string;
  ciudad: string | null;
  categoria: string | null;
  idPartner: string | null;
  /** true = el cliente actual ya sigue este local (no se puede volver a agregar). */
  yaSeguido: boolean;
}

export interface CreateCompetitorProductInput {
  productId: string;
  storeId: string;
  externalUrl: string;
  cssSelector?: string;
}

export interface CreateDataSourceInput {
  name: string;
  type: DataSourceType;
  apiUrl?: string;
  apiHeaders?: Record<string, string>;
  apiResponsePath?: string;
  refreshIntervalMinutes?: number;
  variableCode: string;
}

// ========================================
// Catalog Scraping
// ========================================

export type CatalogScrapeStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface ScrapedCatalogJob {
  id: string;
  status: CatalogScrapeStatus;
  scraperType: string;
  totalProducts: number;
  scrapedCount: number;
  matchedCount: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  storeId: string;
  store: {
    id: string;
    name: string;
    url: string;
    logoUrl: string | null;
    isOwnCatalog?: boolean;
    includeInAnalysis?: boolean;
    /** Plan 298 (E10): la tienda la lee Prixia. */
    prixiaStoreId?: string | null;
  };
  _count: { items: number };
  createdAt: string;
  phase1Collections: Array<{ slug: string; urls: string[] }> | null;
  /** Plan 291: este barrido es el reparto de una lectura de Prixia. */
  prixiaReadingId?: string | null;
  /** Productos propios vinculados con la tienda de este catálogo (la definición única, L4). */
  vinculadosDeLaTienda?: number;
  /** Un intento más nuevo que este catálogo completo (falló o sigue corriendo), L4. */
  ultimoIntento?: UltimoIntentoDeCatalogo | null;
}

export interface UltimoIntentoDeCatalogo {
  id: string;
  status: CatalogScrapeStatus;
  errorMessage: string | null;
  createdAt: string;
  phase1Collections: Array<{ slug: string; urls: string[] }> | null;
}

export interface ScrapedCatalogItem {
  id: string;
  externalId: string;
  name: string;
  sku: string | null;
  brand: string | null;
  category: string | null;
  price: number;
  listPrice: number | null;
  imageUrl: string | null;
  productUrl: string | null;
  isAvailable: boolean;
  isIgnored: boolean;
  matchedProductId: string | null;
  matchedProduct: { id: string; name: string; sku: string; currentPrice: number } | null;
  skuExistsAsProduct?: boolean;
  jobId: string;
  job: { id: string; store: { id: string; name: string } };
  createdAt: string;
}

export interface CatalogItemsResponse {
  items: ScrapedCatalogItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  categories: string[];
}

export interface CatalogScrapeProgress {
  jobId: string;
  status: CatalogScrapeStatus;
  totalProducts: number;
  scrapedCount: number;
  matchedCount: number;
  errorMessage: string | null;
  logs?: { t: string; msg: string }[];
}

export interface ScraperType {
  type: string;
  name: string;
}

// ========================================
// Inference / Auto-Match
// ========================================

export type MatchSuggestionStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';

export interface MatchSuggestion {
  id: string;
  confidence: number;
  matchReasons: string[];
  /** L2: cuándo se puntuó (null = motor viejo) y cuánta evidencia hubo, 0 a 100. */
  scoredAt?: string | null;
  evidence?: number | null;
  status: MatchSuggestionStatus;
  catalogItemId: string;
  catalogItem: {
    id: string;
    name: string;
    sku: string | null;
    brand: string | null;
    category: string | null;
    price: number;
    imageUrl: string | null;
    productUrl: string | null;
  };
  productId: string;
  product: {
    id: string;
    name: string;
    sku: string;
    category: string | null;
    currentPrice: number;
    imageUrl: string | null;
  };
  jobId: string;
  job?: { id: string; store: { id: string; name: string } };
  createdAt: string;
}

export interface SuggestionsResponse {
  suggestions: MatchSuggestion[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Los números del embudo de la pestaña Inferencia. */
export interface InferenceStats {
  suggestions: { pending: number; accepted: number; rejected: number };
  /**
   * Desde qué puntaje una propuesta se marca como «segura». En retail es el corte
   * aprendido de la TIENDA (null = todavía no hay «seguras»); en comida, 70. Lo
   * decide el backend — ver `shared.tsx`.
   */
  umbralAlta?: number | null;
  /** L2: desde qué puntaje se muestra por defecto; lo de abajo casi nunca es el mismo producto. */
  umbralRevisar?: number;
  rubro?: 'RETAIL' | 'COMIDA';
  /** L2: cuándo se puntuó por última vez este catálogo (null = nunca con el motor nuevo). */
  puntuadoEl?: string | null;
  /**
   * Calidad de lo pendiente medida por la MEJOR opción de cada producto:
   * >= `umbralAlta` / 40 a `umbralAlta`-1 / <40. Los tres tramos suman
   * `groups.products`.
   */
  confidence: { high: number; medium: number; low: number };
  /**
   * Las decisiones que hay por delante, contadas por PRODUCTO propio y no por
   * sugerencia: un producto con seis candidatos es una decisión, no seis.
   * `unique`/`multi` son la cuenta vieja, por item de la tienda.
   */
  groups: {
    unique: number;
    multi: number;
    /** Productos propios con candidatos esperando. */
    products?: number;
    /** Productos ya vinculados y descartados en este catálogo. */
    acceptedProducts?: number;
    rejectedProducts?: number;
    /** De esos, los que tienen una sola opción: son una confirmación. */
    singleOption?: number;
    /** Y los que tienen varias y hay que elegir cuál. */
    contested: number;
    /**
     * Los productos tuyos a los que el motor no les encontró NADA. Cuidado con
     * el vocabulario: no es "la tienda no lo vende", es "no se encontró
     * parecido", que puede ser una cosa o la otra.
     */
    withoutOption?: number;
    /** Se aceptaron y hoy no tienen vínculo vigente (dados de baja o desvinculados), L4. */
    aceptadosSinVinculo?: number;
  };
  /** `lastPriceUpdate` es del monitoreo de precios, NO del scrape del catálogo. */
  coverage: {
    linked: number;
    /** Productos propios más que se podrían vincular hoy, sin contar los ya vinculados. */
    awaiting?: number;
    totalProducts: number;
    lastPriceUpdate: string | null;
  };
}

/**
 * Cómo va una inferencia en curso.
 *
 * `corriendo: false` NO es lo mismo que "va en cero": significa que no hay
 * ninguna corriendo, y la pantalla no tiene que dibujar barra alguna.
 */
export type InferenceProgress =
  | { corriendo: false }
  | {
      corriendo: true;
      /** Ronda L2-E (ítem 2): todavía espera su turno en la cola. */
      enCola?: boolean;
      fase: 'indexando' | 'analizando' | 'guardando';
      total: number;
      procesados: number;
      encontradas: number;
      segundos: number;
    };

/**
 * Un producto propio con los candidatos de la tienda que lo están esperando.
 * Puede tener uno solo —una confirmación— o varios peleándoselo.
 */
export interface ProductGroup {
  product: MatchSuggestion['product'];
  suggestions: MatchSuggestion[];
  /**
   * Con qué item de la tienda está vinculado HOY. Solo viaja en la solapa de
   * ya vinculados. `name` puede faltar si el vínculo viene de un catálogo
   * anterior y esa URL ya no está en el actual: ausente es "no sé", no un
   * nombre inventado.
   */
  linkedItem?: {
    name: string | null;
    url: string;
    price: number | null;
    imageUrl: string | null;
  } | null;
  /**
   * Vinculado con un catálogo anterior: sigue vigente, pero el catálogo actual
   * no le ofrece ninguna opción. Explica por qué esa fila no se puede cambiar.
   */
  notInCurrentCatalog?: boolean;
  /**
   * La tienda cambió el artículo de un vínculo vigente de este producto y una persona tiene
   * que confirmar el cambio (hay una propuesta esperando). El vínculo sigue como estaba.
   */
  cambioDeArticulo?: boolean;
  /**
   * 9D-4: en una fila marcada «cambió el artículo», a qué está vinculado el producto HOY (el
   * vínculo sigue como estaba). `name` ausente = no se sabe; nunca un nombre inventado.
   */
  vinculoActual?: { name: string | null; url: string; price: number | null } | null;
  /**
   * 9D-5: la persona descartó el artículo nuevo de la tienda y el vínculo sigue con su último precio
   * y no recibió uno nuevo desde esta fecha (ISO). Sólo viaja en «Ya vinculados».
   */
  cambioDescartadoDesde?: string | null;
  maxConfidence: number;
  /** El mejor empata con el segundo: el sistema no tiene con qué elegir. */
  noClearWinner: boolean;
  /** Cuántos comparten el puntaje más alto. Con 90/90/50 son dos, no tres. */
  tiedAtTop: number;
}

export interface ProductGroupsResponse {
  groups: ProductGroup[];
  /** Todos los productos con candidatos esperando. */
  totalGroups: number;
  /** Los que tienen una sola opción: no hay pelea, pero hay que confirmarlos. */
  withSingleCandidate: number;
  /** Los que varios items se pelean. Sobre TODO el job, no sobre la página. */
  contested: number;
  /** L2: desde qué puntaje se le avisa a una persona que se lleva una propuesta segura. */
  umbralParaConfirmar?: number;
  /**
   * Los de este catálogo que YA tienen precio de la tienda. No están en la
   * cola: es el número de la solapa "Ya vinculados", y llega en las dos vistas.
   */
  alreadyLinked: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Un producto tuyo al que el buscador no le encontró nada en el catálogo. */
export interface ProductWithoutOption {
  id: string;
  name: string;
  sku: string;
  category: string | null;
  currentPrice: number;
  imageUrl: string | null;
}

export interface ProductsWithoutOptionsResponse {
  products: ProductWithoutOption[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * C4b (vault 280): un artículo del catálogo de la tienda encontrado a mano desde la
 * Inferencia (`GET /catalog/stores/:storeId/buscar-articulos`).
 */
export interface ArticuloDelCatalogo {
  id: string;
  nombre: string;
  marca: string | null;
  codigo: string | null;
  precio: number;
  precioLista: number | null;
  disponible: boolean;
  imagenUrl: string | null;
  url: string | null;
  /** El OTRO producto de la empresa con el que el artículo ya está vinculado. */
  vinculadoCon: { id: string; nombre: string; codigo: string } | null;
  /** Ya está vinculado con el producto que se está vinculando. */
  esElVinculoActual: boolean;
  /** La última decisión de una persona sobre este par es «no es». */
  unaPersonaDijoQueNoEs: boolean;
}

export interface BusquedaEnCatalogo {
  tienda: { id: string; nombre: string };
  /** `null` = la tienda todavía no tiene un catálogo completo. */
  catalogo: { id: string; fecha: string | null } | null;
  producto: { id: string; nombre: string; tieneVinculo: boolean };
  articulos: ArticuloDelCatalogo[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Hacia dónde se ordena la lista. */
export type SortOrder = 'asc' | 'desc';

/**
 * Por qué columna se piden ordenados los productos. Hay una por cada dato que
 * la fila muestra: `ownName`/`ownPrice` son los del producto propio y
 * `name`/`price` los de su mejor opción en la tienda. El criterio vive en el
 * backend (`suggestion-grouping.service.ts`).
 */
export type SuggestionSort = 'confidence' | 'priceDiff' | 'name' | 'ownName' | 'price' | 'ownPrice';

/**
 * La dirección con la que arranca cada columna al elegirla por primera vez.
 * Tiene que coincidir con la del backend, porque es la que decide si mandar
 * `order` o dejar que lo resuelva el servidor.
 */
export const ORDEN_NATURAL: Record<SuggestionSort, SortOrder> = {
  confidence: 'desc',
  priceDiff: 'desc',
  price: 'desc',
  ownPrice: 'desc',
  name: 'asc',
  ownName: 'asc',
};

export interface UnmatchedItem {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  price: number;
  bestScore: number;
}

export interface InferenceResult {
  analyzed: number;
  generated: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
  skipped: number;
  totalCatalogItems: number;
  ownProductCount: number;
  unmatchedItems: UnmatchedItem[];
}

/** Una fila del CSV cuyo precio rompe una regla (sin la de movimiento: decisión 7). */
export interface FilaFueraDeReglas {
  sku: string;
  precio: number;
  motivo: string;
  rompe: Array<{
    ruleId: string;
    ruleName: string;
    tipo: string;
    min: number | null;
    max: number | null;
  }>;
}

/** Una fila cuyo precio la estrategia va a volver a cambiar (decisión 4). */
export interface ConflictoCsvConEstrategia {
  sku: string;
  precio: number;
  estrategia: { id: string; name: string };
  propone: number;
}

/** Plan 267: lo que se ignoró sin frenar la carga (columnas retiradas o desconocidas, códigos repetidos). */
export interface AvisoDeCargaArchivo {
  row: number;
  sku: string;
  message: string;
}

export interface ImportCsvResult {
  created: number;
  updated: number;
  /** Plan 267: productos que ya estaban igual. Ausente con un servidor viejo. */
  unchanged?: number;
  avisos?: AvisoDeCargaArchivo[];
  errors: { row: number; sku: string; message: string }[];
  /** Plan 4A: hasta 200. `null` = no se pudieron revisar las reglas (los precios se guardaron igual). */
  fueraDeReglas?: FilaFueraDeReglas[] | null;
  fueraDeReglasTotal?: number | null;
  /** Plan 4B-datos T12. */
  conflictosConEstrategia?: ConflictoCsvConEstrategia[] | null;
  conflictosConEstrategiaTotal?: number | null;
  /** Reglas de la demo (343): el código del tope de la prueba si alguna fila no entró por él. */
  codigo?: string;
  /** Por qué no se pudieron revisar o marcar las reglas (null = todo bien). Ausente con un servidor viejo. */
  avisoReglas?: string | null;
}

/** POST /products/import/revisar (4B-datos T12): lo mismo que el import, sin escribir. */
export interface RevisionDeCsv {
  filas: number;
  nuevos: number;
  actualizados: number;
  /** Plan 267: productos que ya estaban igual. */
  sinCambios?: number;
  avisos?: AvisoDeCargaArchivo[];
  cambianDePrecio: number;
  errors: { row: number; sku: string; message: string }[];
  /** Lote C1 (punto 10): filas con el costo en 0 (entran como sin costo). Ausente con un servidor viejo. */
  costoEnCero?: number;
  fueraDeReglas: FilaFueraDeReglas[] | null;
  fueraDeReglasTotal: number | null;
  conflictosConEstrategia: ConflictoCsvConEstrategia[] | null;
  conflictosConEstrategiaTotal: number | null;
  /** Reglas de la demo (343): el código del tope de la prueba si alguna fila no entraría por él. */
  codigo?: string;
  /** Por qué no se pudieron revisar las reglas (null = se revisaron). Ausente con un servidor viejo. */
  avisoReglas?: string | null;
}

// ========================================
// Monitor Runs (Historial de monitoreo)
// ========================================

export type MonitorRunType = 'PRICE_CHECK' | 'FULL_RESCRAPE';
/** SKIPPED (L3, vault 276): el chequeo no se hizo y la corrida dice por qué. */
export type MonitorRunStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'SKIPPED';

export interface PriceChange {
  productName: string;
  productUrl?: string | null;
  oldPrice: number;
  newPrice: number;
  changePercent: number;
  oldListPrice?: number | null;
  newListPrice?: number | null;
  // 'PROMO' = sólo se movió el precio con promo (toggle), 'REAL' = cambió el precio de lista.
  changeType?: 'REAL' | 'PROMO';
  ownProductId?: string | null;
  ownProductName?: string | null;
  ownProductSku?: string | null;
}

export type MonitorRunTrigger = 'SCHEDULED' | 'MANUAL';

export interface MonitorRun {
  id: string;
  type: MonitorRunType;
  status: MonitorRunStatus;
  storeId: string;
  store: { id: string; name: string; url: string; logoUrl: string | null };
  productsChecked: number;
  priceChanges: number;
  alertsGenerated: number;
  errorMessage: string | null;
  /** L3 (vault 276): respuestas con dato. Ausentes (o en 0) en las corridas de antes. */
  readOk?: number;
  /** L3: productos que la tienda no respondió. */
  readFailed?: number;
  /** L3: productos que la tienda dijo que ya no existen. */
  notFound?: number;
  /** L3: por qué se salteó (corrida SKIPPED), en castellano para la persona. */
  skippedReason?: string | null;
  changes:
    | {
        items: PriceChange[];
        total: number;
        truncated: boolean;
        realTotal?: number;
        promoTotal?: number;
      }
    | { jobId: string }
    | null;
  /** Origen de la corrida: programada (calendario) o manual. null = legacy. */
  trigger: MonitorRunTrigger | null;
  /** Tarea programada que la originó (si aplica). */
  scheduledTaskId: string | null;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  createdAt: string;
}

export interface MonitorHealth {
  activeTasks: number;
  nextRun: { at: string; storeName: string; type: ScheduledTaskType } | null;
  runs24h: number;
  successPct24h: number | null;
  changes24h: number;
}

export interface MonitorRunsResponse {
  runs: MonitorRun[];
  total: number;
  page: number;
  totalPages: number;
  /** Con los demás filtros, sin «Solo con cambios» (pastilla «Todas las revisiones»). */
  totalTodas?: number;
  /** Con los demás filtros, sólo las que cambiaron algo (pastilla «Solo con cambios»). */
  totalConCambios?: number;
}

export interface MonitorRunsFilters {
  storeId?: string;
  type?: MonitorRunType;
  trigger?: MonitorRunTrigger;
  scheduledTaskId?: string;
  from?: string;
  to?: string;
  /** Sólo las revisiones con algún cambio de precio (lo filtra el servidor). */
  conCambios?: boolean;
  page?: number;
  limit?: number;
}

// ========================================
// ANALYTICS — Gráficos / Dashboard
// ========================================

export interface PriceHistoryPoint {
  date: string;
  price: number;
  changePercent: number | null;
}

export type ScraperSegmentStatus = 'ok' | 'degraded' | 'down' | 'running';

export interface ScraperStatusSegment {
  status: ScraperSegmentStatus;
  startedAt: string;
  type: string;
  trigger: string | null;
  productsChecked: number;
  priceChanges: number;
  durationMs: number | null;
  errorMessage: string | null;
}

export interface ScraperStatusStore {
  storeId: string;
  storeName: string;
  scraperType: string | null;
  monitorEnabled: boolean;
  currentStatus: 'operational' | 'degraded' | 'down' | 'running' | 'unknown';
  uptimePct: number | null;
  totalRuns: number;
  okCount: number;
  failCount: number;
  lastRunAt: string | null;
  lastFailure: { startedAt: string; errorMessage: string | null } | null;
  segments: ScraperStatusSegment[];
}

export interface ScraperStatusResponse {
  days: number;
  stores: ScraperStatusStore[];
}

export interface CompetitorStoreHistory {
  storeId: string;
  storeName: string;
  storeUrl: string;
  /** 26-oct: la página del producto en esa tienda (vínculo vigente). */
  productUrl?: string | null;
  storeLogoUrl: string | null;
  lastScrapedPrice: number | null;
  lastScrapedAt: string | null;
  points: {
    date: string;
    price: number;
    oldPrice: number;
    changePercent: number;
    /**
     * Precio de lista vigente en ese punto. Dice si el precio ESTÁ en promoción
     * (lista > precio) y hasta cuándo; null = la tienda no reportó lista.
     */
    listPrice?: number | null;
    /** Clasificación de ingesta del cambio ('REAL' | 'PROMO'); null si no se registró. */
    changeType?: 'REAL' | 'PROMO' | null;
    /** Ronda F de L4: del vínculo vigente o de uno anterior (otro artículo, atenuado). */
    vinculo?: 'vigente' | 'anterior';
  }[];
}

export interface PriceHistoryResponse {
  product: {
    id: string;
    name: string;
    sku: string;
    currentPrice: number;
    lastCalculatedPrice: number | null;
  };
  calculatedHistory: {
    recommendedPrice: number;
    currentPrice: number;
    competitorMinPrice: number | null;
    competitorAvgPrice: number | null;
    calculatedAt: string;
  }[];
  competitorByStore: {
    storeId: string;
    storeName: string;
    points: { date: string; price: number; changePercent: number | null }[];
  }[];
  days: number;
  sources: { calculatedPrices: number; monitorRuns: number; competitorPricePoints: number };
}

export interface CompetitorHistoryResponse {
  product: { id: string; name: string; sku: string };
  stores: CompetitorStoreHistory[];
  days: number;
  totalPoints: number;
}

export interface PricePositionProduct {
  id: string;
  name: string;
  sku: string;
  category: string | null;
  currentPrice: number;
  /** 26-oct («Mi negocio»): la página del producto en la tienda propia. */
  productUrl?: string | null;
  lastCalculatedPrice: number | null;
  strategy: PriceStrategy;
  competitorMinPrice: number | null;
  competitorAvgPrice: number | null;
  /**
   * Cuánto más caro (+) o más barato (−) estoy que el competidor más barato, sobre MI
   * precio (L4, PD-28). Es el mismo número que la celda de Productos con el signo dado
   * vuelta: Productos habla de «ellos», Gráficos de «yo».
   */
  gapVsMin: number | null;
  competitorCount: number;
  /** Precios dentro de la ventana que parecen de otro producto (revisar vínculo). */
  revisarVinculoCount?: number;
  stores: {
    storeId: string;
    storeName: string;
    price: number | null;
    /** «Confirmado el» (L3). */
    lastScrapedAt: string | null;
    externalUrl: string | null;
    matchScoreAtLink?: number | null;
    /** Dentro de la ventana de frescura de la empresa. */
    fresco?: boolean;
    /** Menos de la mitad o más del doble de mi precio. */
    revisarVinculo?: boolean;
    /** Entra en el mínimo y el promedio (fresco y no sospechoso). */
    usable?: boolean;
  }[];
}

export interface PricePositionSummary {
  totalProducts: number;
  productsWithCompetitors: number;
  productsIAmCheapest: number;
  pctIAmCheapest: number | null;
  avgGapVsMin: number | null;
  activeStoresCount: number;
  /** Precios que no se usan por viejos (fuera de la ventana de la empresa). */
  preciosViejos?: number;
  /** Precios que no se usan porque parecen de otro producto. */
  preciosRevisarVinculo?: number;
  frescuraDias?: number;
}

export interface PricePositionResponse {
  products: PricePositionProduct[];
  total: number;
  summary: PricePositionSummary;
}

export interface TopChangeItem {
  productName: string;
  ownProductId: string | null;
  ownProductName: string | null;
  ownProductSku: string | null;
  storeId: string;
  storeName: string;
  productUrl: string | null;
  timesChanged: number;
  maxChangePercent: number;
  avgChangePercent: number;
  lastChange: { oldPrice: number; newPrice: number; changePercent: number; date: string };
  /** Clasificación del último cambio. null = sin insumo para clasificar (no se muestra badge). */
  lastChangeType?: 'NOISE' | 'PROMO' | 'REPRICE' | 'BIG_MOVE' | null;
  allChanges: {
    oldPrice: number;
    newPrice: number;
    changePercent: number;
    date: string;
    type?: 'NOISE' | 'PROMO' | 'REPRICE' | 'BIG_MOVE';
  }[];
}

export interface TopChangesResponse {
  items: TopChangeItem[];
  total: number;
  from: string;
  to: string;
  runsAnalyzed: number;
}

// ========================================
// Nuevos endpoints de analytics (2026-05-15)
// ========================================

export interface MarketVolatilityPoint {
  date: string;
  changesCount: number;
  avgChangePercent: number;
}

export interface MarketVolatilitySeries {
  storeId: string;
  storeName: string;
  points: MarketVolatilityPoint[];
}

export interface MarketVolatilityResponse {
  series: MarketVolatilitySeries[];
  from: string;
  to: string;
}

export interface MarginByCategoryItem {
  category: string;
  productCount: number;
  avgRealizedMargin: number;
  avgTargetMargin: number | null;
  belowTarget: number;
  belowTargetPct: number | null;
}

export interface MarginByCategoryResponse {
  categories: MarginByCategoryItem[];
  totalWithCost: number;
  dataNote: string;
}

export interface StoreVolatilityItem {
  storeId: string;
  storeName: string;
  storeLogoUrl: string | null;
  runsAnalyzed: number;
  totalChanges: number;
  avgChangePercent: number;
  maxChangePercent: number;
  linkedChanges: number;
}

export interface StoreVolatilityResponse {
  stores: StoreVolatilityItem[];
  days: number;
}

export interface CoverageByCategoryItem {
  category: string;
  totalProducts: number;
  withMonitoring: number;
  withoutMonitoring: number;
  coveragePct: number;
  /** L7 (vault 279, D12): alguna tienda vende algo parecido y la empresa no la excluyó. */
  comparable: boolean;
  motivoNoComparable: 'SIN_COMPETIDOR' | 'EXCLUIDA' | null;
}

export interface CoverageByCategoryResponse {
  categories: CoverageByCategoryItem[];
  overall: {
    totalProducts: number;
    withMonitoring: number;
    coveragePct: number;
  };
  /** L7: la cobertura sobre los productos comparables; `null` = sin dato. */
  comparable: {
    totalProducts: number;
    withMonitoring: number;
    coveragePct: number | null;
    categoriasExcluidas: number;
  };
}

// ========================================
// Scheduled Tasks (Calendario de scraping)
// ========================================

export type ScheduledTaskType = 'PRICE_CHECK' | 'FULL_RESCRAPE';
export type IntervalUnit = 'HOURS' | 'DAYS' | 'WEEKS';

export interface ScheduledTask {
  id: string;
  tenantId: string;
  storeId: string;
  type: ScheduledTaskType;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  preferredHour: number | null;
  preferredMin: number;
  startAt: string;
  endAt: string | null;
  isActive: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunId: string | null;
  store?: { name: string };
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledTaskAgendaItem {
  taskId: string;
  storeId: string;
  storeName: string;
  type: ScheduledTaskType;
  runAt: string;
}

export interface CreateScheduledTaskInput {
  storeId: string;
  type: ScheduledTaskType;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  preferredHour?: number | null;
  preferredMin?: number;
  startAt: string;
  endAt?: string | null;
  isActive?: boolean;
}

/** L2: una corrida de la Inferencia en segundo plano. */
export interface CorridaDeInferencia {
  id: string;
  estado: 'EN_COLA' | 'CALCULANDO' | 'LISTO' | 'FALLO';
  fase?: string | null;
  total?: number;
  procesados?: number;
  encontradas?: number;
  generadas?: number | null;
  seguras?: number | null;
  error?: string | null;
  empezadaEn?: string | null;
  terminadaEn?: string | null;
}

/** GET /api/dynamic-pricing/resumen (L4, vault 277, PD-37). */
export interface ResumenPrecios {
  ventanaHoras: number;
  salud: {
    corridas: number;
    completadas: number;
    fallidas: number;
    salteadas: number;
    exitoPct: number | null;
    cambios: number;
  };
  cambios: {
    /** Productos propios DISTINTOS con un cambio real de la competencia en la ventana. */
    productos: number;
    ultimos: Array<{
      productId: string;
      productName: string;
      storeName: string;
      productUrl: string | null;
      oldPrice: number;
      newPrice: number;
      changePercent: number;
      scrapedAt: string;
    }>;
  };
  tiendas: Array<{
    storeId: string;
    storeName: string;
    storeUrl: string | null;
    ultimaCorrida: null | {
      type: MonitorRunType;
      status: MonitorRunStatus;
      productsChecked: number;
      readOk: number;
      readFailed: number;
      notFound: number;
      skippedReason: string | null;
      errorMessage: string | null;
      durationMs: number | null;
      startedAt: string;
      priceChanges: number;
    };
  }>;
}
